import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { CSVErrorReport } from '~components/CSVImportExport/CSVErrorReport';
import { CSVFieldReference } from '~components/CSVImportExport/CSVFieldReference';
import { CSVFileDropzone } from '~components/CSVImportExport/CSVFileDropzone';
import { CSVImportButton } from '~components/CSVImportExport/CSVImportButton';
import { CSVImportProgress } from '~components/CSVImportExport/CSVImportProgress';
import { CSVParsePreview } from '~components/CSVImportExport/CSVParsePreview';
import { CSVParseSummary } from '~components/CSVImportExport/CSVParseSummary';
import getCsvImportInstructions from '~components/CSVImportExport/getCsvImportInstructions';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useCSVImportExport } from '~hooks/useCSVImportExport/useCSVImportExport';
import useEntityCsvConfig, {
  EntityCsvConfigKey,
} from '~hooks/useCSVImportExport/useEntityCsvConfig';
import { COMPANY_ONBOARDING_RECORDS_QUERY_KEY } from '~queries/useCompanyOnboardingRecordsQuery';
import { useStores } from '~store';
import { Alert, alert, AlertTypes } from '~types/AlertTypes';

import { SecondaryText, StepCallout } from './CompanyOnboardingPrimitives';

interface CompanyOnboardingCsvImportZoneProps {
  companyId: string;
  configKey: EntityCsvConfigKey;
  entityLabel: string;
  stepKey: CompanyOnboardingStepKey;
}

const CompanyOnboardingCsvImportZone = ({
  companyId,
  configKey,
  entityLabel,
  stepKey,
}: CompanyOnboardingCsvImportZoneProps) => {
  const { toasterStore } = useStores();
  const queryClient = useQueryClient();
  const { config, loadConfig } = useEntityCsvConfig(configKey, companyId);

  const toast = (message: string, kind: Alert['kind']) =>
    toasterStore.push(alert(message, kind));

  const {
    downloadFailedRows,
    downloadTemplate,
    fileSelectionId,
    importConfig,
    importData,
    importResult,
    isImporting,
    isLoading,
    parseFile,
    parseResult,
    progress,
    reset,
    selectedFileName,
  } = useCSVImportExport({
    companyId,
    config,
    loadConfig,
    onError: (error) => toast(error.message, AlertTypes.error),
    onSuccess: (result) => {
      const failedCount = result.failed.length + (parseResult?.invalidRows.length ?? 0);
      AnalyticsController.track('company_onboarding_csv_import_completed', {
        failed_count: failedCount,
        step_key: stepKey,
        succeeded_count: result.succeeded.length,
      });
      if (failedCount) {
        toast(
          t('csv_import_export.import_complete_with_failures', {
            failed: failedCount,
            succeeded: result.succeeded.length,
          }),
          AlertTypes.warn,
        );
      } else {
        toast(t('csv_import_export.import_complete'), AlertTypes.success);
      }
      void queryClient.invalidateQueries({
        queryKey: [COMPANY_ONBOARDING_RECORDS_QUERY_KEY],
      });
    },
  });

  const hasOnlyInvalidRows = Boolean(
    parseResult?.invalidRows.length && !parseResult.validRows.length,
  );

  if (importResult || hasOnlyInvalidRows) {
    return (
      <CSVErrorReport
        importResult={importResult}
        onClose={reset}
        onDownloadFailedRows={downloadFailedRows}
        parseResult={parseResult}
        resultSummary={
          importResult && importConfig.summarizeResults?.(importResult.succeeded)
        }
        rowLabel={importConfig.rowLabel}
      />
    );
  }

  if (isImporting && progress) {
    return <CSVImportProgress progress={progress} />;
  }

  return (
    <Box data-test-id="company-onboarding-csv-import-zone">
      <StepCallout
        body={t('company_onboarding.import.template_body')}
        title={t('company_onboarding.import.template_title')}
      >
        <SecondaryText sx={{ mt: 1 }}>
          {getCsvImportInstructions(config, entityLabel)}
        </SecondaryText>
        <SmallButton
          color="secondary"
          data-test-id="company-onboarding-csv-download-template"
          onClick={downloadTemplate}
          startIcon={<DescriptionOutlined />}
          sx={{ mt: 1.5 }}
          variant="outlined"
        >
          {t('csv_import_export.download_template')}
        </SmallButton>
        <CSVFieldReference columns={config.columns} entityType={config.entityType} />
      </StepCallout>
      <CSVFileDropzone
        disabled={isLoading}
        fileSelectionId={fileSelectionId}
        onClear={reset}
        onSelect={parseFile}
        selectedFileName={selectedFileName}
      />

      {parseResult ? (
        <>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 2 }}>
            <CSVParseSummary parseResult={parseResult} sx={{ flexGrow: 1 }} />
            <CSVImportButton
              entityDisplayName={entityLabel}
              isImporting={isImporting}
              onImport={() => importData(parseResult.validRows)}
              validRowCount={parseResult.validRows.length}
            />
          </Box>
          <CSVParsePreview
            columns={config.columns}
            parseResult={parseResult}
            sx={{ mt: 2 }}
          />
        </>
      ) : null}
    </Box>
  );
};

export default CompanyOnboardingCsvImportZone;
